
import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useMainLinks } from '@/hooks/use-main-links';
import { useLinks } from '@/hooks/use-links';
import { ProfilePreview } from '@/components/link-short/ProfilePreview';
import { MainLink, ThemeSettings } from '@/types/link-short';
import { supabase } from '@/integrations/supabase/client';

export function PublicLinkPage() {
  const { slug } = useParams<{ slug: string }>();
  const { fetchMainLinkBySlug, incrementMainLinkClicks } = useMainLinks();
  const [mainLink, setMainLink] = useState<MainLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  
  // Use the links hook only after we have the main link
  const { links, incrementLinkClicks } = useLinks(mainLink?.id || '');

  const safeParseThemeSettings = (themeSettings: any): ThemeSettings => {
    console.log('Parsing theme settings:', themeSettings);
    
    if (!themeSettings || (typeof themeSettings !== 'object' && !Array.isArray(themeSettings))) {
      return {
        backgroundType: 'solid',
        backgroundColor: '#f8fafc',
        textColor: '#1e293b',
        buttonStyle: 'filled',
        buttonBgColor: '#3b82f6',
        buttonTextColor: '#ffffff',
        borderRadius: '0.5rem'
      } as ThemeSettings;
    }

    // Se for um array, pegar o primeiro elemento ou usar defaults
    if (Array.isArray(themeSettings)) {
      const settings = themeSettings[0] || {};
      return {
        backgroundType: settings.backgroundType || 'solid',
        backgroundColor: settings.backgroundColor || '#f8fafc',
        textColor: settings.textColor || '#1e293b',
        buttonStyle: settings.buttonStyle || 'filled',
        buttonBgColor: settings.buttonBgColor || '#3b82f6',
        buttonTextColor: settings.buttonTextColor || '#ffffff',
        borderRadius: settings.borderRadius || '0.5rem'
      } as ThemeSettings;
    }

    // Se for um objeto, fazer parse normal
    return {
      backgroundType: themeSettings.backgroundType || 'solid',
      backgroundColor: themeSettings.backgroundColor || '#f8fafc',
      textColor: themeSettings.textColor || '#1e293b',
      buttonStyle: themeSettings.buttonStyle || 'filled',
      buttonBgColor: themeSettings.buttonBgColor || '#3b82f6',
      buttonTextColor: themeSettings.buttonTextColor || '#ffffff',
      borderRadius: themeSettings.borderRadius || '0.5rem',
      backgroundGradient: themeSettings.backgroundGradient,
      backgroundImageUrl: themeSettings.backgroundImageUrl,
      cardBackgroundColor: themeSettings.cardBackgroundColor,
      fontFamily: themeSettings.fontFamily,
      avatarStyle: themeSettings.avatarStyle || 'circle',
      layout: themeSettings.layout || 'default',
      buttonAnimation: themeSettings.buttonAnimation || 'none',
      iconPosition: themeSettings.iconPosition || 'left',
      showBio: themeSettings.showBio !== false,
      iconSize: themeSettings.iconSize || 'medium'
    } as ThemeSettings;
  };

  useEffect(() => {
    const loadMainLink = async () => {
      if (!slug) {
        console.log('No slug provided');
        setNotFound(true);
        setLoading(false);
        return;
      }

      console.log('Loading main link for slug:', slug);
      console.log('Current URL:', window.location.href);

      try {
        // First try to find by the exact slug
        console.log('Trying to fetch by slug:', slug);
        let foundLink = await fetchMainLinkBySlug(slug);
        console.log('Result from fetchMainLinkBySlug:', foundLink);
        
        // If not found and slug looks like a UUID, try using it as an ID
        if (!foundLink && slug.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
          console.log('Slug looks like UUID, trying to fetch by ID:', slug);
          const { data, error } = await supabase
            .from('main_links')
            .select('*')
            .eq('id', slug)
            .eq('is_active', true)
            .single();
          
          console.log('Direct DB query result:', { data, error });
          
          if (data) {
            foundLink = {
              id: data.id,
              user_id: data.user_id,
              title: data.title,
              slug: data.slug,
              description: data.description,
              url: data.url,
              is_active: data.is_active,
              clicks: data.clicks,
              theme_settings: safeParseThemeSettings(data.theme_settings),
              bg_color: data.bg_color,
              text_color: data.text_color,
              avatar_url: data.avatar_url,
              created_at: data.created_at,
              updated_at: data.updated_at
            };
          }
        }
        
        console.log('Final found link:', foundLink);
        
        if (foundLink) {
          setMainLink(foundLink);
          
          // Fetch user profile information
          console.log('Fetching user profile for user_id:', foundLink.user_id);
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('name, avatar_url, agency_name')
            .eq('id', foundLink.user_id)
            .single();
          
          console.log('User data result:', { userData, userError });
          
          if (userData) {
            setUserProfile(userData);
          }
          
          // Increment click count when page is accessed
          await incrementMainLinkClicks(foundLink.id);
        } else {
          console.log('Link not found for slug:', slug);
          
          // Let's also check what links exist in the database for debugging
          const { data: allLinks } = await supabase
            .from('main_links')
            .select('id, slug, title, is_active')
            .limit(10);
          
          console.log('Available links in database:', allLinks);
          setNotFound(true);
        }
      } catch (error) {
        console.error('Error loading main link:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    loadMainLink();
  }, [slug, fetchMainLinkBySlug, incrementMainLinkClicks]);

  const handleLinkClick = async (link: any) => {
    // Increment click count for individual links
    await incrementLinkClicks(link.id);
    // Open the link
    window.open(link.url, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (notFound || !mainLink) {
    console.log('Rendering 404 - notFound:', notFound, 'mainLink:', mainLink);
    return <Navigate to="/404" replace />;
  }

  return (
    <div className="min-h-screen">
      <ProfilePreview
        mainLink={mainLink}
        links={links}
        userProfile={userProfile}
        isPreview={true}
        onLinkClick={handleLinkClick}
      />
    </div>
  );
}
